// @ts-check
import { gql } from '@apollo/client';
import { Image } from '@react-pdf/renderer';
import React from 'react';
import { Box, Flex, Text, Title, useTheme } from '../../../pdf-design-system';
import { usePage } from '../../../pdf-design-system/Base/PageLayout/Page';
import {
  useRegistration,
  Provider,
} from '../../../pdf-design-system/Registration';
import QRCode from '../components/LogoQRCode';
import { nameLine1, nameLine2 } from '../exhibitor';

/**
 * Renders a single table.
 *
 * @param {{style?: object}} props
 */
export default function VieVinumTable({ style }) {
  const { alignment } = usePage();
  /** @type {Registration} */
  const table = useRegistration();
  return (
    <Provider value={afterQuery(table)}>
      <Box
        style={[{ height: '48mm', marginBottom: '6.7mm' }, style]}
        wrap={false}
      >
        <Flex direction={alignment === 'left' ? 'row' : 'row-reverse'}>
          <TableHeader
            style={[
              { height: '11mm', width: '51mm' },
              alignment === 'left'
                ? { marginRight: '3mm' }
                : { marginLeft: '3mm' },
            ]}
          />
          <ExhibitorHeader style={{ height: '11mm', width: '84mm' }} />
        </Flex>
        <Flex
          direction={alignment === 'left' ? 'row' : 'row-reverse'}
          style={{ paddingTop: '2.5mm' }}
        >
          <LogoContent
            style={[
              { width: '51mm' },
              alignment === 'left'
                ? { marginRight: '3mm' }
                : { marginLeft: '3mm' },
            ]}
          />
          <ExhibitorContent style={{ flexGrow: 1 }} />
        </Flex>
      </Box>
    </Provider>
  );
}

function TableHeader({ ...rest }) {
  const { alignment } = usePage();
  const { theme } = useTheme();
  /** @type {Table & { stand: Table["stand"] & { number: string; } }} */
  const table = useRegistration();
  return (
    <Box {...rest}>
      <Flex
        style={[
          {
            height: '100%',
            paddingVertical: '1mm',
            paddingHorizontal: '1.3mm',
            backgroundColor: theme.colors.primary,
            color: theme.colors.onPrimary,
          },
          alignment === 'left'
            ? { marginLeft: '-12mm', paddingLeft: '12mm', flexDirection: 'row' }
            : {
                marginRight: '-12mm',
                paddingRight: '12mm',
                flexDirection: 'row-reverse',
              },
        ]}
      >
        <Box style={{ flexGrow: 1, marginTop: 'auto' }}>
          {table.hatPremiumeintrag && (
            <>
              <Text
                style={{ marginLeft: alignment === 'left' ? 0 : 'auto' }}
                variant="medium"
              >
                Premiumeintrag
              </Text>
              <Text
                style={{
                  marginLeft: alignment === 'left' ? 0 : 'auto',
                  maxWidth: '38mm',
                  textAlign: alignment,
                }}
              >
                {premiumeintragText(table)}
              </Text>
            </>
          )}
        </Box>
        <Box style={{ textTransform: 'uppercase' }}>
          <Text
            style={{
              marginLeft: alignment === 'left' ? 'auto' : 0,
              marginRight: '0.7mm',
              letterSpacing: '0.5pt',
            }}
          >
            {table.stand?.number ? 'Tisch' : ''}
          </Text>
          <Title
            variant="large"
            style={{ marginLeft: alignment === 'left' ? 'auto' : 0 }}
          >
            {table.stand?.number ?? table.stand?.name}
          </Title>
        </Box>
      </Flex>
    </Box>
  );
}

/** @param {Table} table */
function premiumeintragText(table) {
  if (!table.hatPremiumeintrag) return '';
  const productsWithAwards = table.registeredProducts.nodes.filter(
    // @ts-ignore awards are arrays in any case
    ({ product }) => product?.awards?.filter(({ award }) => award).length > 0
  );
  const types = [
    table.profile.offeringTypes?.includes('onlineShop') && 'Shop',
    table.profile.offeringTypes?.includes('videos') && 'Videos',
    table.profile.offeringTypes?.includes('imageGallery') && 'Bildergalerie',
    productsWithAwards?.length && 'Auszeichnungen',
  ].filter(Boolean);
  if (types.length === 0) return 'Mit Zusatzinfo online';
  if (types.length === 1) return `Mit ${types[0]}`;
  return `Mit ${types.slice(0, -1).join(', ')} und ${types[types.length - 1]}`;
}

function ExhibitorHeader({ style, ...rest }) {
  const { theme } = useTheme();
  /** @type {Table} */
  const table = useRegistration();
  return (
    <Flex
      {...rest}
      style={[
        {
          height: '100%',
          paddingBottom: '2mm',
          borderBottom: 1,
          borderBottomColor: theme.colors.primary,
          color: theme.colors.primary,
        },
        style,
      ]}
    >
      <Box
        style={{
          flex: '7 1 auto',
          marginRight: '1mm',
          marginTop: 'auto',
          maxHeight: '100%', // Make sure it doesn't overflow.
        }}
      >
        <Title>{table.nameLine1}</Title>
        <Title>{table.nameLine2}</Title>
      </Box>
      <Text
        variant="medium"
        style={{
          marginTop: 'auto',
          marginBottom: '-0.6mm',
          maxWidth: '30mm',
          flex: '2 0 auto',
          textAlign: 'right',
        }}
      >
        {table.profile?.region}
      </Text>
    </Flex>
  );
}

function LogoContent({ style, ...rest }) {
  const { alignment } = usePage();

  /** @type {Table} */
  const table = useRegistration();
  const profile = table.profile || {};
  return (
    <Flex
      style={style}
      direction={alignment === 'left' ? 'row' : 'row-reverse'}
      {...rest}
    >
      <Box
        style={[
          { width: '15.5mm' },
          alignment === 'left'
            ? { marginRight: '2.5mm' }
            : { marginLeft: '2.5mm' },
        ]}
      >
        {table.companyId && (
          <>
            {table.siteUrl ? (
              <QRCode
                url={`${table.siteUrl}/e/${table.companyId}`
                  // Uppercasing the URL makes it simpler to encode, resulting in less complexity.
                  .toUpperCase()}
                style={{
                  width: '15.5mm',
                  height: '15.5mm',
                  marginBottom: '2mm',
                }}
                margin={0}
                logo="unserwein"
              />
            ) : (
              <QRCode
                url={`http://app.bottlebooks.me`}
                style={{
                  width: '15.5mm',
                  height: '15.5mm',
                  marginBottom: '2mm',
                }}
                margin={0}
                logo="unserwein"
              />
            )}
            <Text
              variant="small"
              style={{
                textAlign: alignment === 'left' ? 'left' : 'right',
                width: '15.5mm',
              }}
            >
              scannen &amp; bewerten
            </Text>
          </>
        )}
      </Box>
      <Box>
        {profile?.logo?.fixed?.src && <Image src={profile.logo.fixed.src} />}
      </Box>
    </Flex>
  );
}

function ExhibitorContent({ ...rest }) {
  /** @type {Table} */
  const { profile, ...custom } = useRegistration();

  return (
    <Flex {...rest}>
      <Box style={{ flex: '1 1 0', marginRight: '2mm' }}>
        <Text>
          {[
            profile?.street,
            [profile?.postalCode, profile?.city].filter(Boolean).join(' '),
            profile?.country && profile?.country !== 'Österreich'
              ? profile?.country
              : null,
          ]
            .filter(Boolean)
            .join(', ')}
        </Text>
        <Text>
          {[
            profile?.telephone,
            profile?.email,
            profile?.website,
            text(custom.websites),
          ]
            .filter(Boolean)
            .join(', ')}
        </Text>
        <Text style={{ marginTop: '2mm' }}>{profile.associations}</Text>
      </Box>
      <Zertifizierungslogos />
    </Flex>
  );
}

function Zertifizierungslogos() {
  /** @type {Table} */
  const table = useRegistration();

  return (
    <Flex style={{ marginRight: '-1mm' }}>
      {table.logos?.map((key, index) => (
        <Image
          key={`${key}-${index}`}
          // 7.5mm in 300dpi (7.5/2.54*300).
          src={`https://res.cloudinary.com/bottlebooks/image/upload/c_pad,h_89,q_auto:best,w_89/v1/vievinum/${key}.jpg`}
          style={{
            marginRight: '1mm',
            width: '7.5mm',
            height: '7.5mm',
          }}
        />
      ))}
    </Flex>
  );
}

/** @type  {import('@apollo/client').TypedDocumentNode<import('./@types/VieVinumTable').VieVinumTableFragment>}*/
VieVinumTable.fragment = gql`
  fragment VieVinumTable on RegistrationCatalogSection {
    registration {
      collection {
        site {
          publicUrl
        }
        # IDs for apollo-client:
        ... on Event {
          eventId
        }
      }
      registrationId
      name
      companyId
      stand {
        name
      }
      exhibitorPackage: customFieldValue(key: "exhibitorPackage") {
        ...VieVinumTable_SelectFieldValue
      }
      zertifizierungslogos: customFieldValue(key: "zertifizierungslogos") {
        ...VieVinumTable_MultiSelectFieldValue
      }
      nameLine1: customFieldValue(key: "nameLine1") {
        ...VieVinumTable_TextFieldValue
      }
      nameLine2: customFieldValue(key: "nameLine2") {
        ...VieVinumTable_TextFieldValue
      }
      websites: customFieldValue(key: "websites") {
        ...VieVinumTable_TextFieldValue
      }
      registeredProducts {
        nodes {
          product {
            productId
            awards {
              award
            }
          }
        }
      }
      profile {
        street
        postalCode
        city
        country
        telephone
        email(format: TEXT)
        website(format: TEXT)
        region
        associations
        offeringTypes
        # Certifications are used as a backup for zertifizierungslogos.
        certifications(format: RAW)
        logo {
          # 33mmx33mm in 300dpi
          fixed(
            width: 392
            height: 392
            crop: PAD
            gravity: NORTH
            fetch_format: JPG
            quality: BEST
          ) {
            src
          }
        }
      }
    }
  }
  fragment VieVinumTable_TextFieldValue on FieldValue {
    __typename
    ... on TextFieldValue {
      value
    }
  }
  fragment VieVinumTable_MultiSelectFieldValue on FieldValue {
    __typename
    ... on MultiSelectFieldValue {
      values {
        key
      }
    }
  }
  fragment VieVinumTable_SelectFieldValue on FieldValue {
    __typename
    ... on SelectFieldValue {
      key
    }
  }
`;

/** @typedef {import('./@types/VieVinumTable').VieVinumTableFragment} RegistrationCatalogSection */
/** @typedef {RegistrationCatalogSection["registration"]} Registration */

/** @param {Registration} table */
function afterQuery(table) {
  const line1 = text(table.nameLine1);
  const line2 = text(table.nameLine2);
  const siteUrl = table.collection?.site?.publicUrl?.replace(/\/$/, '');
  // const siteUrl = 'https://vv.unserwein.at';
  return {
    ...table,
    nameLine1: nameLine1(table.name, line1, line2),
    nameLine2: nameLine2(table.name, line1, line2),
    logos: zertifizierungslogos(
      table.zertifizierungslogos,
      // @ts-ignore certifications aren't defined in the model yet.
      table.profile?.certifications
    ),
    hatPremiumeintrag: hatPremiumeintrag(table),
    siteUrl,
  };
}

/**
 * @typedef {ReturnType<afterQuery>} Table
 */
/** @returns {string} */
function text(customField) {
  return customField?.value || '';
}
/**
 * @param {Registration["zertifizierungslogos"]} zertifizierungslogos
 * @param {string[] | null} certifications
 * @returns {('bio' | 'demeter' | 'sektAustria' | 'nachhaltigAustria')[] | undefined}
 */
function zertifizierungslogos(zertifizierungslogos, certifications) {
  if (zertifizierungslogos?.__typename === 'MultiSelectFieldValue') {
    if (zertifizierungslogos?.values?.length) {
      // @ts-ignore key is one of the four possible values above.
      return zertifizierungslogos?.values?.map(({ key }) => key);
    }
  }
  return certifications
    ?.map((certification) => certificationMapping[certification])
    .filter(Boolean);
}
const certificationMapping = {
  certifiedOrganicEU: 'bio',
  nachhaltig_austria: 'nachhaltigAustria',
  osterreichischer_sekt: 'sektAustria',
  demeter: 'demeter',
};

/**
 * @param {Registration} table
 */
function hatPremiumeintrag(table) {
  if (table.exhibitorPackage?.__typename === 'SelectFieldValue') {
    if (table.exhibitorPackage?.key === 'premium') return true;
    if (table.exhibitorPackage?.key === 'pavilion') return true;
  }
  return false;
}
